import { transparentize } from 'polished';
import c from '../colors';
import { setAlpha } from '../utils';

/**
 * @see http://livingston.sas.com/10-0/essentials/secondary/
 */
export const stateSecondary = {
  normal(theme) {
    switch (theme.baseReportThemeName) {
      case 'highcontrast2018':
      case 'highcontrast2017':
        return {
          fg: c.white(theme),
          bg: c.black(theme),
          bd: c.secondary(theme)
        };
      case 'light2025':
      case 'dark2025':
      case 'highcontrast2020':
        return {
          fg: theme.tokens?.['color-action-text-standard-normal'],
          bg: theme.tokens?.['color-action-background-standard-normal'],
          bd: theme.tokens?.['color-action-border-standard-normal']
        };
    }
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: c.white(theme),
          bg: transparentize(0.6, c.e1(theme)),
          bd: c.e(theme)
        };
      case 'high-contrast':
        return {
          fg: c.e2(theme),
          bg: c.black(theme),
          bd: c.e2(theme)
        };
      default:
        return {
          fg: c.e(theme),
          bg: transparentize(0.9, c.e(theme)),
          bd: c.e(theme)
        };
    }
  },
  selected(theme) {
    switch (theme.baseReportThemeName) {
      case 'highcontrast2018':
      case 'highcontrast2017':
        return {
          fg: c.black(theme),
          bg: c.secondary(theme),
          bd: 'transparent'
        };
      case 'light2025':
      case 'dark2025':
      case 'highcontrast2020':
        return {
          fg: theme.tokens?.['color-action-text-standard-selected'],
          bg: theme.tokens?.['color-action-background-standard-selected'],
          bd: theme.tokens?.['color-action-border-standard-selected']
        };
      case 'marine':
      case 'sail':
        return {
          fg: c.e(theme),
          bg: c.n3(theme),
          bd: c.e(theme)
        };
    }
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: c.white(theme),
          bg: transparentize(0.6, c.e1(theme)),
          bd: c.e(theme)
        };
      case 'high-contrast':
        return {
          fg: c.black(theme),
          bg: c.e2(theme),
          bd: c.black(theme)
        };
      default:
        return {
          fg: c.e(theme),
          bg: transparentize(0.75, c.e(theme)),
          bd: c.e(theme)
        };
    }
  },
  disabledSelected(theme) {
    switch (theme.baseReportThemeName) {
      case 'highcontrast2018':
      case 'highcontrast2017':
        return {
          fg: c.black(theme),
          bg: c.hcDisabled(theme),
          bd: 'transparent'
        };
      case 'light2025':
      case 'dark2025':
      case 'highcontrast2020':
        return {
          fg: theme.tokens?.['color-action-text-standard-selected-disabled'],
          bg: theme.tokens?.['color-action-background-standard-selected-disabled'],
          bd: theme.tokens?.['color-action-border-standard-selected-disabled']
        };
    }
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: transparentize(0.7, c.white(theme)),
          bg: transparentize(0.7, c.e1(theme)),
          bd: 'transparent'
        };
      case 'high-contrast':
        return {
          fg: transparentize(0.5, c.e2(theme)),
          bg: transparentize(0.5, c.black(theme)),
          bd: transparentize(0.5, c.e2(theme))
        };
      default:
        return {
          fg: transparentize(0.8, c.e(theme)),
          bg: transparentize(0.95, c.e(theme)),
          bd: transparentize(0.8, c.e(theme))
        };
    }
  },
  disabled(theme) {
    switch (theme.baseReportThemeName) {
      case 'highcontrast2018':
      case 'highcontrast2017':
        return {
          fg: c.hcDisabled(theme),
          bg: c.black(theme),
          bd: c.hcDisabled(theme)
        };
      case 'light2025':
      case 'dark2025':
      case 'highcontrast2020':
        return {
          fg: theme.tokens?.['color-action-text-standard-disabled'],
          bg: theme.tokens?.['color-action-background-standard-disabled'],
          bd: theme.tokens?.['color-action-border-standard-disabled']
        };
    }
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: transparentize(0.8, c.white(theme)),
          bg: 'transparent',
          bd: transparentize(0.8, c.white(theme))
        };
      case 'high-contrast':
        return {
          fg: transparentize(0.5, c.white(theme)),
          bg: transparentize(0.5, c.black(theme)),
          bd: transparentize(0.5, c.white(theme))
        };
      default:
        return {
          fg: c.n2(theme),
          bg: 'transparent',
          bd: c.n2(theme)
        };
    }
  }
};

/**
 * @see http://livingston.sas.com/10-0/essentials/secondary/
 */
export const stateSecondaryToggle = {
  normal(theme) {
    switch (theme.baseReportThemeName) {
      case 'light2025':
      case 'dark2025':
      case 'highcontrast2020':
        return {
          fg: theme.tokens?.['color-action-text-standard-normal'],
          bg: theme.tokens?.['color-action-background-standard-normal'],
          bd: theme.tokens?.['color-action-border-standard-normal']
        };
    }
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: c.white(theme),
          bg: c.p_3(theme),
          bd: transparentize(0.6, c.white(theme))
        };
      case 'high-contrast':
        return {
          fg: c.white(theme),
          bg: c.black(theme),
          bd: c.white(theme)
        };
      default:
        return {
          fg: c.n_3(theme),
          bg: c.white(theme),
          bd: c.n_1(theme)
        };
    }
  },
  selected(theme) {
    switch (theme.baseReportThemeName) {
      case 'highcontrast2018':
      case 'highcontrast2017':
        return {
          fg: c.black(theme),
          bg: c.secondary(theme),
          bd: 'transparent'
        };
      case 'light2025':
      case 'dark2025':
      case 'highcontrast2020':
        return {
          fg: theme.tokens?.['color-action-text-standard-selected'],
          bg: theme.tokens?.['color-action-background-standard-selected'],
          bd: theme.tokens?.['color-action-border-standard-selected']
        };
    }
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: c.white(theme),
          bg: c.e1(theme),
          bd: c.e(theme)
        };
      case 'high-contrast':
        return {
          fg: c.black(theme),
          bg: c.e2(theme),
          bd: c.black(theme)
        };
      default:
        return {
          fg: c.white(theme),
          bg: c.e(theme),
          bd: c.e(theme)
        };
    }
  },
  disabledSelected(theme) {
    switch (theme.baseReportThemeName) {
      case 'highcontrast2018':
      case 'highcontrast2017':
        return {
          fg: c.black(theme),
          bg: c.hcDisabled(theme),
          bd: 'transparent'
        };
      case 'light2025':
      case 'dark2025':
      case 'highcontrast2020':
        return {
          fg: theme.tokens?.['color-action-text-standard-selected-disabled'],
          bg: theme.tokens?.['color-action-background-standard-selected-disabled'],
          bd: theme.tokens?.['color-action-border-standard-selected-disabled']
        };
    }
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: transparentize(0.7, c.white(theme)),
          bg: transparentize(0.5, c.e1(theme)),
          bd: 'transparent'
        };
      case 'high-contrast':
        return {
          fg: transparentize(0.5, c.black(theme)),
          bg: transparentize(0.5, c.e2(theme)),
          bd: transparentize(0.5, c.black(theme))
        };
      default:
        return {
          fg: transparentize(0.3, c.white(theme)),
          bg: transparentize(0.7, c.e(theme)),
          bd: 'transparent'
        };
    }
  },
  disabled(theme) {
    switch (theme.baseReportThemeName) {
      case 'highcontrast2018':
      case 'highcontrast2017':
        return {
          fg: c.hcDisabled(theme),
          bg: c.black(theme),
          bd: c.hcDisabled(theme)
        };
      case 'light2025':
      case 'dark2025':
      case 'highcontrast2020':
        return {
          fg: theme.tokens?.['color-action-text-standard-disabled'],
          bg: theme.tokens?.['color-action-background-standard-disabled'],
          bd: theme.tokens?.['color-action-border-standard-disabled']
        };
    }
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: transparentize(0.8, c.white(theme)),
          bg: 'transparent',
          bd: transparentize(0.8, c.white(theme))
        };
      case 'high-contrast':
        return {
          fg: transparentize(0.5, c.white(theme)),
          bg: transparentize(0.5, c.black(theme)),
          bd: transparentize(0.5, c.white(theme))
        };
      default:
        return {
          fg: c.n2(theme),
          bg: 'transparent',
          bd: c.n2(theme)
        };
    }
  }
};

/**
 * Temporary doc until the new Design System site is up
 * @see https://gitlab.sas.com/novacommons/nova-tokens/-/tree/master/tokens
 */

export const stateSecondaryNova = {
  normal(theme) {
    switch (theme.baseThemeName) {
      case 'high-contrast':
        return {
          fg: c.colorTextDefault(theme),
          bg: c.colorUiLight(theme),
          bd: c.colorNeutral(theme)
        };
      default:
        return {
          fg: c.colorTextDefault(theme),
          bg: setAlpha(c.colorUiLight(theme), 0.1),
          bd: c.colorNeutral(theme)
        };
    }
  },
  selected(theme) {
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: c.colorTextDefault(theme),
          bg: setAlpha(c.colorSecondary(theme), 0.25),
          bd: c.colorSecondary(theme)
        };
      case 'high-contrast':
        return {
          fg: c.colorTextInverse(theme),
          bg: c.colorSecondary(theme),
          bd: c.colorSecondary(theme)
        };
      default:
        return {
          fg: c.colorTextSecondary(theme),
          bg: setAlpha(c.colorSecondary(theme), 0.25),
          bd: c.colorSecondary(theme)
        };
    }
  },
  disabledSelected(theme) {
    switch (theme.baseThemeName) {
      case 'high-contrast':
        return {
          fg: setAlpha(c.colorTextInverse(theme), 0.64),
          bg: setAlpha(c.colorSecondary(theme), 0.32),
          bd: 'transparent'
        };
      case 'dark':
        return {
          fg: setAlpha(c.colorTextDefault(theme), 0.32),
          bg: setAlpha(c.colorSecondary(theme), 0.16),
          bd: 'transparent'
        };
      default:
        return {
          fg: setAlpha(c.colorSecondary(theme), 0.32),
          bg: setAlpha(c.colorSecondary(theme), 0.08),
          bd: 'transparent'
        };
    }
  },
  disabled(theme) {
    switch (theme.baseThemeName) {
      case 'high-contrast':
        return {
          fg: setAlpha(c.colorTextDefault(theme), 0.48),
          bg: setAlpha(c.colorNeutral(theme), 0.16),
          bd: 'transparent'
        };
      default:
        return {
          fg: setAlpha(c.colorTextDefault(theme), 0.32),
          bg: setAlpha(c.colorNeutral(theme), 0.08),
          bd: 'transparent'
        };
    }
  }
};

/**
 * Temporary doc until the new Design System site is up
 * @see https://gitlab.sas.com/novacommons/nova-tokens/-/tree/master/tokens
 */

export const stateSecondaryToggleNova = {
  normal(theme) {
    return {
      fg: c.colorTextDefault(theme),
      bg: c.colorUiLight(theme),
      bd: c.colorNeutral(theme)
    };
  },
  selected(theme) {
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: c.colorTextDefault(theme),
          bg: c.colorSecondary(theme),
          bd: c.colorSecondary(theme)
        };
      default:
        return {
          fg: c.colorTextInverse(theme),
          bg: c.colorSecondary(theme),
          bd: c.colorSecondary(theme)
        };
    }
  },
  disabledSelected(theme) {
    switch (theme.baseThemeName) {
      case 'high-contrast':
        return {
          fg: setAlpha(c.colorTextInverse(theme), 0.32),
          bg: setAlpha(c.colorSecondary(theme), 0.24),
          bd: 'transparent'
        };
      case 'dark':
        return {
          fg: setAlpha(c.colorTextDefault(theme), 0.32),
          bg: setAlpha(c.colorSecondary(theme), 0.24),
          bd: 'transparent'
        };
      default:
        return {
          fg: setAlpha(c.colorTextInverse(theme), 0.64),
          bg: setAlpha(c.colorSecondary(theme), 0.16),
          bd: 'transparent'
        };
    }
  },
  disabled(theme) {
    return {
      fg: setAlpha(c.colorTextDefault(theme), 0.32),
      bg: c.colorUiLight(theme),
      bd: setAlpha(c.colorNeutral(theme), 0.32)
    };
  },
  readOnly(theme) {
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: c.colorTextDefault(theme),
          bg: c.colorUiMedium(theme),
          bd: setAlpha(stateSecondaryToggleNova.normal(theme).bd, 0.3)
        };
      default:
        return {
          fg: c.colorTextDefault(theme),
          bg: c.colorUiLight(theme),
          bd: setAlpha(stateSecondaryToggleNova.normal(theme).bd, 0.3)
        };
    }
  }
};
//# sourceMappingURL=secondary-states.js.map